import { describeConditions, formatCedis, type Conditions } from '@accra/shared';
import type { EventBus } from '../../core/events';
import { UI_TEXT } from '../../content/lines/ui';
import { activeRoute } from '../../world/route';
import { el } from '../components/el';

/** At most this many toasts on screen; older ones make way, so they never pile up over the road. */
const MAX_TOASTS = 2;
/** Police fines (red light, zebra, checkpoint) stay up longer than ordinary toasts. */
const FINE_TOAST_SECONDS = 4.5;

/** Toasts, the "Now entering" banner and the mate's speech bubble, all driven by game events. */
export class Feedback {
  private readonly toasts = el('div', 'toasts');
  private readonly banner = el('div', 'banner');
  private readonly bubble = el('div', 'mate-bubble');
  private readonly fade = el('div', 'uturn-fade');
  private bannerTimer = 0;
  private bubbleTimer = 0;

  constructor(parent: HTMLElement) {
    parent.append(this.fade, this.toasts, this.banner, this.bubble);
  }

  bind(events: EventBus): void {
    events.on('leg:entered', (e) => this.showBanner(e.name, e.subtitle));
    events.on('trip:started', (e) => {
      if (e.trip === 1) return this.showBanner(`${e.from} → ${e.to}`, 'Trip 1', 'Today\'s route');
      // The U-turn itself is seen on the road; a quick fade just hides the street changing over to the other side.
      this.fade.classList.remove('go');
      void this.fade.offsetWidth;
      this.fade.classList.add('go');
      this.showBanner(`Back to ${e.to}`, `Trip ${e.trip} · U-turn at ${e.from}`, 'Other side of the road');
    });
    events.on('mate:say', (e) => this.say(e.text));
    events.on('passenger:dropped', (e) => this.toast(`+${formatCedis(e.farePesewas + e.tipPesewas)}`, 'cash'));
    events.on('stop:rated', (e) => {
      if (e.rating === 'perfect') this.toast('PERFECT STOP!', 'good');
      if (e.rating === 'overshot') this.toast('YOU PASS THE STOP!', 'bad');
    });
    events.on('passenger:missed', (e) => this.toast(`${e.count} missed ${e.stopName} · no fare`, 'bad'));
    events.on('run:closeCall', () => this.toast('CLOSE! +50', 'good'));
    events.on('run:stumbled', (e) => this.toast(`SCRAPE! Repairs −${formatCedis(e.repairPesewas)}`, 'bad'));
    events.on('run:collision', (e) =>
      this.toast(e.rearEnd ? `REAR-END! Police fine −${formatCedis(e.finePesewas)}` : `CRASH! Repairs −${formatCedis(e.repairPesewas)}`, 'bad'),
    );
    // The power chip already shows it; phones hide this toast (CSS) to keep the road clear.
    events.on('powerUp:started', (e) => this.toast(`${e.name}! ${Math.round(e.seconds)} s`, 'good power'));
    events.on('pickup:star', (e) => this.toast(`×${e.factor} SCORE! ${e.seconds} s`, 'good power'));
    events.on('pickup:gift', (e) =>
      this.toast(e.coins > 0 ? `GIFT! +${e.coins} coins` : e.repaired > 0 ? `FITTER! Repairs −${Math.round(e.repaired)}%` : 'FITTER! Trotro is fine', 'good'),
    );
    events.on('fuel:low', (e) => this.toast(`⛽ ${Math.round(e.units)}% FUEL LEFT!${e.station ? ` ${e.station} in ${e.distance} m` : ''}`, 'bad', FINE_TOAST_SECONDS));
    events.on('fuel:empty', () => this.toast('⛽ OUT OF FUEL!', 'bad', FINE_TOAST_SECONDS));
    events.on('journey:arrived', () => this.toast(`🏁 ARRIVED IN ${activeRoute().to.toUpperCase()}! AYEKOO!`, 'good', FINE_TOAST_SECONDS));
    events.on('fuel:full', () => this.toast('⛽ TANK FULL', 'good'));
    events.on('checkpoint:ran', (e) => this.toast(`POLICE! DROVE THROUGH A CHECKPOINT −${formatCedis(e.finePesewas)}`, 'bad', FINE_TOAST_SECONDS));
    events.on('checkpoint:dash', (e) => this.toast(`DASH −${formatCedis(e.amountPesewas)} · ALL GOOD!`, 'good', FINE_TOAST_SECONDS));
    events.on('checkpoint:cleared', (e) => e.dashed ? undefined : this.toast(e.papersProblem ? `PAPERS PROBLEM −${formatCedis(e.finePesewas)}` : 'PAPERS OK! Go on', e.papersProblem ? 'bad' : 'good', FINE_TOAST_SECONDS));
    events.on('powerUp:saved', (e) => this.toast(e.id === 'shield' ? 'GYE NYAME! Shield took the hit' : 'COMOT! +20', 'good'));
    events.on('junction:pedestrianFine', (e) => this.toast(`DIDN'T STOP FOR PEDESTRIANS −${formatCedis(e.finePesewas)}`, 'bad', FINE_TOAST_SECONDS));
    events.on('junction:ranRed', (e) => this.toast(`POLICE FINE −${formatCedis(e.finePesewas)}`, 'bad', FINE_TOAST_SECONDS));
  }

  /** At the start of a shift: what the weather and light will do to the drive. */
  announceConditions(conditions: Conditions): void {
    const words = describeConditions(conditions);
    if (words) this.toast(`${words.headline} ${words.detail}`, 'good');
  }

  reset(): void {
    this.toasts.replaceChildren();
    this.banner.classList.remove('show');
    this.bubble.classList.remove('show');
  }

  update(dt: number): void {
    this.bannerTimer -= dt;
    this.bubbleTimer -= dt;
    if (this.bannerTimer <= 0) this.banner.classList.remove('show');
    if (this.bubbleTimer <= 0) this.bubble.classList.remove('show');
  }

  /** `seconds` is how long it stays: police fines linger so they can be read. */
  private toast(text: string, tone: 'good' | 'bad' | 'cash' | 'good power', seconds = 1.6): void {
    const toast = el('div', `toast ${tone}`, text);
    toast.style.animationDuration = `${seconds}s`;
    this.toasts.append(toast);
    while (this.toasts.childElementCount > MAX_TOASTS) this.toasts.firstElementChild?.remove();
    setTimeout(() => toast.remove(), seconds * 1000);
  }

  private showBanner(name: string, subtitle: string, heading: string = UI_TEXT.nowEntering): void {
    this.banner.replaceChildren(el('small', '', heading), el('strong', '', name), el('span', '', subtitle));
    this.banner.classList.add('show');
    this.bannerTimer = 2.6;
  }

  private say(text: string): void {
    this.bubble.replaceChildren(el('small', '', 'MATE'), el('span', '', text));
    this.bubble.classList.add('show');
    this.bubbleTimer = 2.8;
  }
}
